import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { PiggyBank, Plus, ArrowDownLeft, ArrowUpRight, Lock, Unlock, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const vaultTypes = [
  { value: "flexible", label: "Flexible", description: "Withdraw anytime" },
  { value: "locked_7", label: "7-Day Lock", description: "Higher rewards" },
  { value: "locked_30", label: "30-Day Lock", description: "Best rewards" },
  { value: "locked_90", label: "90-Day Lock", description: "Maximum rewards" },
];

const currencies = [
  { code: "USD", symbol: "$" },
  { code: "KES", symbol: "KSh" },
  { code: "UGX", symbol: "USh" },
  { code: "TZS", symbol: "TSh" },
  { code: "RWF", symbol: "FRw" },
];

export default function VaultsPage() {
  const [vaults, setVaults] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedVault, setSelectedVault] = useState(null);
  const [actionType, setActionType] = useState("deposit");
  const [actionAmount, setActionAmount] = useState("");

  const [newVault, setNewVault] = useState({
    name: "",
    target_amount: "",
    currency: "USD",
    vault_type: "flexible",
  });

  const fetchData = async () => {
    try {
      const [vaultsRes, walletRes] = await Promise.all([
        axios.get(`${API}/vaults`, { withCredentials: true }),
        axios.get(`${API}/wallet`, { withCredentials: true }),
      ]);
      setVaults(vaultsRes.data);
      setWallet(walletRes.data);
    } catch (error) {
      toast.error("Failed to fetch vaults");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateVault = async (e) => {
    e.preventDefault();
    if (!newVault.name || !newVault.target_amount) {
      toast.error("Please fill all fields");
      return;
    }

    setCreateLoading(true);
    try {
      await axios.post(
        `${API}/vaults`,
        {
          name: newVault.name,
          target_amount: parseFloat(newVault.target_amount),
          currency: newVault.currency,
          vault_type: newVault.vault_type,
        },
        { withCredentials: true }
      );
      toast.success("Vault created successfully!");
      setNewVault({ name: "", target_amount: "", currency: "USD", vault_type: "flexible" });
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create vault");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleVaultAction = async () => {
    if (!actionAmount || parseFloat(actionAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setActionLoading(selectedVault.vault_id);
    try {
      const endpoint = actionType === "deposit" ? "deposit" : "withdraw";
      await axios.post(
        `${API}/vaults/${selectedVault.vault_id}/${endpoint}`,
        { amount: parseFloat(actionAmount) },
        { withCredentials: true }
      );
      toast.success(`${actionType === "deposit" ? "Deposited" : "Withdrawn"} successfully!`);
      setActionAmount("");
      setActionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openActionDialog = (vault, type) => {
    setSelectedVault(vault);
    setActionType(type);
    setActionAmount("");
    setActionDialogOpen(true);
  };

  const getProgress = (current, target) => {
    if (target === 0) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const isLocked = (vault) => {
    if (!vault.locked_until) return false;
    return new Date(vault.locked_until) > new Date();
  };

  const getCurrencySymbol = (code) => currencies.find((c) => c.code === code)?.symbol || code;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="vaults-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Savings Vaults</h1>
            <p className="text-slate-600 mt-1">Save towards your goals</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="create-vault-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Vault
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Vault</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateVault} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Vault Name</Label>
                  <Input
                    placeholder="e.g., Emergency Fund"
                    value={newVault.name}
                    onChange={(e) => setNewVault({ ...newVault, name: e.target.value })}
                    className="h-12"
                    data-testid="vault-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Amount</Label>
                    <Input
                      type="number"
                      placeholder="1000"
                      value={newVault.target_amount}
                      onChange={(e) => setNewVault({ ...newVault, target_amount: e.target.value })}
                      className="h-12"
                      data-testid="vault-target-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select
                      value={newVault.currency}
                      onValueChange={(value) => setNewVault({ ...newVault, currency: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="vault-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Vault Type</Label>
                  <Select
                    value={newVault.vault_type}
                    onValueChange={(value) => setNewVault({ ...newVault, vault_type: value })}
                  >
                    <SelectTrigger data-testid="vault-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {vaultTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          <div>
                            <span className="font-medium">{t.label}</span>
                            <span className="text-slate-500 ml-2">- {t.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                  disabled={createLoading}
                  data-testid="create-vault-submit-btn"
                >
                  {createLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <PiggyBank className="h-4 w-4 mr-2" />
                  )}
                  Create Vault
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Vaults Grid */}
        {vaults.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vaults.map((vault) => (
              <Card key={vault.vault_id} className="border-slate-200 overflow-hidden" data-testid={`vault-${vault.vault_id}`}>
                <div className={`h-2 ${isLocked(vault) ? "bg-amber-500" : "bg-[#10B981]"}`}></div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">{vault.name}</h3>
                      <Badge variant="secondary" className="mt-1">
                        {isLocked(vault) ? (
                          <><Lock className="h-3 w-3 mr-1" /> Locked</>
                        ) : (
                          <><Unlock className="h-3 w-3 mr-1" /> Flexible</>
                        )}
                      </Badge>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-green-50 flex items-center justify-center">
                      <PiggyBank className="h-5 w-5 text-[#10B981]" />
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-end justify-between">
                      <span className="text-2xl font-bold text-slate-900">
                        {getCurrencySymbol(vault.currency)} {vault.current_amount.toLocaleString()}
                      </span>
                      <span className="text-sm text-slate-500">
                        of {getCurrencySymbol(vault.currency)} {vault.target_amount.toLocaleString()}
                      </span>
                    </div>
                    <Progress value={getProgress(vault.current_amount, vault.target_amount)} className="h-2" />
                    <p className="text-xs text-slate-500">
                      {getProgress(vault.current_amount, vault.target_amount).toFixed(1)}% of goal
                    </p>
                  </div>

                  {isLocked(vault) && vault.locked_until && (
                    <p className="text-xs text-amber-600 mb-4">
                      Unlocks on {new Date(vault.locked_until).toLocaleDateString()}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                      onClick={() => openActionDialog(vault, "deposit")}
                      data-testid={`vault-deposit-${vault.vault_id}`}
                    >
                      <ArrowDownLeft className="h-4 w-4 mr-1" />
                      Deposit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => openActionDialog(vault, "withdraw")}
                      disabled={isLocked(vault)}
                      data-testid={`vault-withdraw-${vault.vault_id}`}
                    >
                      <ArrowUpRight className="h-4 w-4 mr-1" />
                      Withdraw
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <PiggyBank className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Vaults Yet</h3>
              <p className="text-slate-500 mb-4">Create your first savings vault to start saving</p>
              <Button
                className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Vault
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === "deposit" ? "Deposit to" : "Withdraw from"} {selectedVault?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">Current Balance</p>
                <p className="text-xl font-bold text-slate-900">
                  {getCurrencySymbol(selectedVault?.currency)} {selectedVault?.current_amount?.toLocaleString()}
                </p>
              </div>

              {actionType === "deposit" && (
                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-slate-500">Available in Wallet</p>
                  <p className="text-xl font-bold text-[#14B8A6]">
                    {getCurrencySymbol(selectedVault?.currency)}{" "}
                    {wallet?.balances
                      ?.find((b) => b.currency === selectedVault?.currency)
                      ?.amount?.toLocaleString() || 0}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={actionAmount}
                  onChange={(e) => setActionAmount(e.target.value)}
                  className="h-12 text-lg"
                  data-testid="vault-action-amount"
                />
              </div>

              <Button
                className="w-full rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                onClick={handleVaultAction}
                disabled={actionLoading === selectedVault?.vault_id}
                data-testid="vault-action-submit"
              >
                {actionLoading === selectedVault?.vault_id ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : actionType === "deposit" ? (
                  <ArrowDownLeft className="h-4 w-4 mr-2" />
                ) : (
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                )}
                {actionType === "deposit" ? "Deposit" : "Withdraw"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
